import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Loader2, AlertTriangle, Zap, Check, X, RotateCw,
} from 'lucide-react';
import {
  fetchMissingIccid, triggerIccidBackfill, triggerIccidBackfillBulk,
  type MissingIccidCharger, type BackfillBulkOutcome,
} from '@/lib/api';
import { chargersBackHref } from '@/lib/chargerScope';

/**
 * Phase 5a Item 5 — ICCID backfill admin tool.
 *
 * Lists chargers missing `sim_iccid` (the 1nce ↔ Charger linkage
 * key) and lets a super_admin trigger an OCPP Reset.req 'Soft'
 * against an idle, healthy charger to force a fresh
 * BootNotification — which the updated handler now persists.
 *
 * Safety guards live server-side (see
 * `apps/api/src/routes/admin/iccid-backfill.ts`): v2-registered
 * flag (CRITICAL), no active session, recent heartbeat, 10-minute
 * per-charger cooldown. This page just surfaces the outcomes.
 */

interface MissingIccidPageProps {
  onNavigate: (path: string) => void;
}

function fmtDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleString();
}

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px',
      fontSize: 11,
      borderRadius: 999,
      background: ok
        ? 'color-mix(in srgb, var(--evi-accent, #2ECC71) 16%, transparent)'
        : 'color-mix(in srgb, var(--evi-error, #ef4444) 16%, transparent)',
      color: ok ? 'var(--evi-accent, #2ECC71)' : 'var(--evi-error, #ef4444)',
    }}>
      {ok ? <Check size={11} /> : <X size={11} />} {label}
    </span>
  );
}

export function MissingIccidPage({ onNavigate }: MissingIccidPageProps) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<BackfillBulkOutcome[] | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [individualResult, setIndividualResult] = useState<{
    charger_id: string; ok: boolean; message?: string;
  } | null>(null);

  const listQ = useQuery({
    queryKey: ['missing-iccid', page],
    queryFn: () => fetchMissingIccid(page, 25),
  });

  const triggerOne = useMutation({
    mutationFn: triggerIccidBackfill,
    onSuccess: (data, chargerId) => {
      setIndividualResult({
        charger_id: chargerId,
        ok: data.accepted,
        message: `Soft reset accepted. Expecting BootNotification within ${data.expected_boot_within_seconds}s.`,
      });
      // Refresh after a 35s grace so the BootNotification has time to land.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['missing-iccid'] }), 35_000);
    },
    onError: (err: unknown, chargerId: string) => {
      const message = err instanceof Error ? err.message : 'Backfill failed';
      setIndividualResult({ charger_id: chargerId, ok: false, message });
    },
  });

  async function runBulk() {
    if (selected.size === 0) return;
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const ids = Array.from(selected);
      const outcomes = await triggerIccidBackfillBulk(ids);
      setBulkResult(outcomes);
      setSelected(new Set());
      setTimeout(() => qc.invalidateQueries({ queryKey: ['missing-iccid'] }), 35_000);
    } catch (err) {
      setBulkResult([{
        charger_id: 'all',
        outcome: 'failed',
        message: err instanceof Error ? err.message : 'Bulk failed',
      }]);
    } finally {
      setBulkRunning(false);
    }
  }

  function toggleSelect(c: MissingIccidCharger) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(c._id)) next.delete(c._id);
      else next.add(c._id);
      return next;
    });
  }

  function selectableInPage(): MissingIccidCharger[] {
    return (listQ.data?.data ?? []).filter(
      (c) => c.is_v2_registered && c.is_active,
    );
  }

  return (
    <div style={{ padding: '24px 28px' }}>
      <button
        type="button"
        onClick={() => onNavigate(chargersBackHref())}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 0', background: 'transparent', border: 'none',
          color: 'var(--color-tx-m)', cursor: 'pointer', fontSize: 12,
          marginBottom: 12,
        }}
      >
        <ArrowLeft size={14} /> Back to chargers
      </button>

      <header style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>ICCID backfill</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-tx-m)' }}>
          Chargers without a SIM ICCID linked. Soft-reset triggers a fresh
          BootNotification that captures the ICCID from the OCPP payload.
        </p>
      </header>

      <div style={{
        padding: '10px 14px',
        background: 'color-mix(in srgb, var(--evi-warning, #f59e0b) 10%, transparent)',
        border: '1px solid color-mix(in srgb, var(--evi-warning, #f59e0b) 35%, transparent)',
        borderRadius: 8,
        fontSize: 12,
        marginBottom: 16,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <strong>Safety</strong>: only v2-registered chargers can be soft-reset
          from here. v1 production chargers are refused with code{' '}
          <code>CHARGER_NOT_V2_REGISTERED</code>. Chargers with an active
          session or stale heartbeat are also refused.
        </div>
      </div>

      {listQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>
          <Loader2 size={14} className="spin" /> Loading…
        </p>
      )}

      {listQ.isError && (
        <p style={{ color: 'var(--evi-error, #ef4444)' }}>
          <AlertTriangle size={14} /> Could not load.
        </p>
      )}

      {listQ.data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--color-tx-m)' }}>
              {listQ.data.meta.total} charger{listQ.data.meta.total === 1 ? '' : 's'} missing ICCID
              · selected: {selected.size}
            </p>
            <button
              type="button"
              onClick={runBulk}
              disabled={selected.size === 0 || bulkRunning}
              style={{
                padding: '8px 14px', fontSize: 13,
                background: 'var(--evi-accent, #2ECC71)', color: '#000',
                border: 'none', borderRadius: 8, fontWeight: 600,
                cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
                opacity: selected.size === 0 ? 0.4 : 1,
              }}
            >
              {bulkRunning
                ? <><Loader2 size={12} className="spin" /> Running…</>
                : <><Zap size={12} style={{ verticalAlign: 'middle' }} /> Trigger backfill ({selected.size})</>}
            </button>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>
                  <input
                    type="checkbox"
                    checked={
                      selectableInPage().length > 0
                      && selectableInPage().every((c) => selected.has(c._id))
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected(new Set(selectableInPage().map((c) => c._id)));
                      } else {
                        setSelected(new Set());
                      }
                    }}
                  />
                </th>
                <th style={{ padding: 8 }}>Identity</th>
                <th style={{ padding: 8 }}>v2</th>
                <th style={{ padding: 8 }}>Active</th>
                <th style={{ padding: 8 }}>Last heartbeat</th>
                <th style={{ padding: 8 }}>Created via</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data.data.map((c) => {
                const eligible = c.is_v2_registered && c.is_active;
                return (
                  <tr key={c._id} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                    <td style={{ padding: 8 }}>
                      <input
                        type="checkbox"
                        disabled={!eligible}
                        checked={selected.has(c._id)}
                        onChange={() => toggleSelect(c)}
                      />
                    </td>
                    <td style={{ padding: 8 }}>
                      <strong>{c.display_name ?? c.identity}</strong>
                      {c.display_name && (
                        <div style={{ fontSize: 11, color: 'var(--color-tx-m)' }}>{c.identity}</div>
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      <StatusBadge ok={c.is_v2_registered} label={c.is_v2_registered ? 'v2' : 'v1'} />
                    </td>
                    <td style={{ padding: 8 }}>
                      <StatusBadge ok={c.is_active} label={c.is_active ? 'active' : 'inactive'} />
                    </td>
                    <td style={{ padding: 8, color: 'var(--color-tx-m)' }}>
                      {fmtDate(c.last_heartbeat)}
                    </td>
                    <td style={{ padding: 8, color: 'var(--color-tx-m)' }}>
                      {c.created_via}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => triggerOne.mutate(c._id)}
                        disabled={!eligible || triggerOne.isPending}
                        style={{
                          padding: '6px 10px', fontSize: 12,
                          background: 'transparent',
                          color: 'var(--evi-accent, #2ECC71)',
                          border: '1px solid var(--evi-accent, #2ECC71)',
                          borderRadius: 6,
                          cursor: eligible ? 'pointer' : 'not-allowed',
                          opacity: eligible ? 1 : 0.4,
                        }}
                      >
                        <RotateCw size={11} style={{ verticalAlign: 'middle' }} />{' '}
                        Trigger
                      </button>
                    </td>
                  </tr>
                );
              })}
              {listQ.data.data.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--color-tx-m)' }}>
                    All chargers have an ICCID linked. 🎉
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
            >
              Previous
            </button>
            <span style={{ padding: '6px 12px', fontSize: 12, color: 'var(--color-tx-m)' }}>
              Page {page} of {Math.max(1, Math.ceil(listQ.data.meta.total / listQ.data.meta.page_size))}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page * listQ.data.meta.page_size >= listQ.data.meta.total}
              style={{ padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Individual result toast */}
      {individualResult && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20,
          padding: '10px 14px',
          background: individualResult.ok
            ? 'color-mix(in srgb, var(--evi-accent, #2ECC71) 18%, var(--color-sf, #111))'
            : 'color-mix(in srgb, var(--evi-error, #ef4444) 18%, var(--color-sf, #111))',
          border: `1px solid ${individualResult.ok ? 'var(--evi-accent, #2ECC71)' : 'var(--evi-error, #ef4444)'}`,
          borderRadius: 8, fontSize: 13, maxWidth: 360,
          boxShadow: '0 4px 18px rgba(0,0,0,0.4)',
        }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>
            {individualResult.ok ? 'Backfill triggered' : 'Backfill refused'}
          </p>
          {individualResult.message && (
            <p style={{ margin: '4px 0 0', fontSize: 11 }}>{individualResult.message}</p>
          )}
          <button
            type="button"
            onClick={() => setIndividualResult(null)}
            style={{
              marginTop: 6, padding: '4px 8px', fontSize: 11,
              background: 'transparent', border: '1px solid currentColor',
              borderRadius: 4, cursor: 'pointer', color: 'inherit',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Bulk result modal */}
      {bulkResult && (
        <div
          role="dialog"
          onClick={() => setBulkResult(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 80,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(640px, 90vw)', maxHeight: '80vh', overflowY: 'auto',
              padding: 20,
              background: 'var(--color-sf, #111)',
              border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 17 }}>Bulk backfill outcomes</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                  <th style={{ textAlign: 'left', padding: 6 }}>Identity</th>
                  <th style={{ textAlign: 'left', padding: 6 }}>Outcome</th>
                  <th style={{ textAlign: 'left', padding: 6 }}>Message</th>
                </tr>
              </thead>
              <tbody>
                {bulkResult.map((o, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                    <td style={{ padding: 6 }}>{o.identity ?? o.charger_id}</td>
                    <td style={{ padding: 6 }}>
                      <span style={{
                        color: o.outcome === 'success'
                          ? 'var(--evi-accent, #2ECC71)'
                          : o.outcome === 'failed'
                            ? 'var(--evi-error, #ef4444)'
                            : 'var(--evi-warning, #f59e0b)',
                      }}>
                        {o.outcome}
                      </span>
                    </td>
                    <td style={{ padding: 6, color: 'var(--color-tx-m)' }}>
                      {o.code ? <><code>{o.code}</code> · </> : null}
                      {o.message ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setBulkResult(null)}
                style={{
                  padding: '8px 14px', fontSize: 13,
                  background: 'var(--evi-accent, #2ECC71)', color: '#000',
                  border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
